import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    latitude: { type: Number, default: 19.076 },
    longitude: { type: Number, default: 72.877 },
    radius: { type: Number, default: 200 },
    zoom: { type: Number, default: 15 },
    readonly: { type: Boolean, default: true },
    latField: String,
    lngField: String,
    radiusField: String
  }

  async connect() {
    this.L = await import("leaflet")

    delete this.L.Icon.Default.prototype._getIconUrl
    this.L.Icon.Default.mergeOptions({
      iconRetinaUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon-2x.png",
      iconUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon.png",
      shadowUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-shadow.png"
    })

    // Defer map creation until the container has non-zero dimensions.
    // In flex/grid layouts (e.g. DaisyUI drawer), the container may be
    // zero-sized when Stimulus connects, causing fragmented tiles.
    if (this.element.clientWidth > 0 && this.element.clientHeight > 0) {
      this._initMap()
    } else {
      this._waitForSize()
    }
  }

  disconnect() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
      this._resizeObserver = null
    }
    if (this.map) this.map.remove()
  }

  _waitForSize() {
    this._resizeObserver = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect
      if (width > 0 && height > 0) {
        this._resizeObserver.disconnect()
        this._resizeObserver = null
        this._initMap()
      }
    })
    this._resizeObserver.observe(this.element)
  }

  _initMap() {
    const L = this.L
    const center = [this.latitudeValue, this.longitudeValue]

    this.map = L.map(this.element).setView(center, this.zoomValue)

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19
    }).addTo(this.map)

    this.marker = L.marker(center, { draggable: !this.readonlyValue }).addTo(this.map)

    this.circle = L.circle(center, {
      radius: this.radiusValue,
      color: "#8290a0",
      fillColor: "#8290a0",
      fillOpacity: 0.1,
      weight: 2
    }).addTo(this.map)

    if (!this.readonlyValue) {
      this.marker.on("dragend", (e) => {
        const pos = e.target.getLatLng()
        this.updateInputs(pos.lat, pos.lng)
        this.circle.setLatLng(pos)
      })
      this.bindExternalInputs()
    }

    // After initial render, watch for further resizes (e.g. sidebar toggle).
    // invalidateSize alone doesn't reposition already-loaded tiles, so we
    // also reset the view to force a full tile re-render.
    this._resizeObserver = new ResizeObserver(() => {
      if (!this.map) return
      this.map.invalidateSize()
      this.map.setView(this.map.getCenter(), this.map.getZoom())
    })
    this._resizeObserver.observe(this.element)
  }

  updateInputs(lat, lng) {
    if (this.latFieldValue) {
      const el = document.getElementById(this.latFieldValue)
      if (el) el.value = lat.toFixed(8)
    }
    if (this.lngFieldValue) {
      const el = document.getElementById(this.lngFieldValue)
      if (el) el.value = lng.toFixed(8)
    }
  }

  bindExternalInputs() {
    if (this.latFieldValue) {
      const latEl = document.getElementById(this.latFieldValue)
      if (latEl) latEl.addEventListener("change", () => this.updateFromInputs())
    }
    if (this.lngFieldValue) {
      const lngEl = document.getElementById(this.lngFieldValue)
      if (lngEl) lngEl.addEventListener("change", () => this.updateFromInputs())
    }
    if (this.radiusFieldValue) {
      const radiusEl = document.getElementById(this.radiusFieldValue)
      if (radiusEl) radiusEl.addEventListener("input", () => {
        const radius = parseInt(radiusEl.value)
        if (!isNaN(radius) && radius > 0) this.circle.setRadius(radius)
      })
    }
  }

  updateFromInputs() {
    const latEl = this.latFieldValue ? document.getElementById(this.latFieldValue) : null
    const lngEl = this.lngFieldValue ? document.getElementById(this.lngFieldValue) : null
    if (!latEl || !lngEl) return

    const lat = parseFloat(latEl.value)
    const lng = parseFloat(lngEl.value)
    if (isNaN(lat) || isNaN(lng)) return

    const pos = this.L.latLng(lat, lng)
    this.marker.setLatLng(pos)
    this.map.setView(pos, this.zoomValue)
    this.circle.setLatLng(pos)
  }
}
